import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Save, ArrowLeft, Sparkles, Plus, Trash2, ExternalLink, Globe, Phone, Mail, Link2 } from "lucide-react";

const TABS = ["English", "繁體中文", "简体中文", "Contact", "Links", "Settings"];

interface CardLink {
  id?: number;
  type: string;
  label: string;
  url: string;
  icon?: string;
  sortOrder?: number;
}

const LINK_TYPES = [
  { value: "website", label: "Website", icon: Globe },
  { value: "linkedin", label: "LinkedIn", icon: Link2 },
  { value: "twitter", label: "Twitter / X", icon: Link2 },
  { value: "instagram", label: "Instagram", icon: Link2 },
  { value: "facebook", label: "Facebook", icon: Link2 },
  { value: "whatsapp", label: "WhatsApp", icon: Phone },
  { value: "email", label: "Email", icon: Mail },
  { value: "phone", label: "Phone", icon: Phone },
  { value: "other", label: "Other", icon: Link2 },
];

export default function CardEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isNew = !id;

  // Determine base path based on current URL (admin vs dashboard)
  const basePath = location.pathname.startsWith("/dashboard") ? "/dashboard" : "/admin";

  const [card, setCard] = useState<any>({
    personal_id: "", status: "active", auth_email: "",
    name_en: "", title_en: "", company_en: "", bio_en: "", department_en: "",
    name_zh_cht: "", title_zh_cht: "", company_zh_cht: "", bio_zh_cht: "", department_zh_cht: "",
    name_zh_chs: "", title_zh_chs: "", company_zh_chs: "", bio_zh_chs: "", department_zh_chs: "",
    email: "", phone: "", website: "", address: "",
    profile_image: "", cover_image: "", theme: "default", custom_css: "",
  });
  const [links, setLinks] = useState<CardLink[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [dirty, setDirty] = useState(false);

  const loadCard = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<{ card: any; links: CardLink[] }>(`/api/v1/cards/${id}`);
      setCard(data.card || data);
      setLinks(data.links || []);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isNew) loadCard();
  }, [isNew, loadCard]);

  const updateField = (field: string, value: string) => {
    setCard((prev: any) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!card.personal_id?.trim()) {
      toast.error("Personal ID is required");
      return;
    }
    setSaving(true);
    try {
      // Map internal state to API camelCase fields
      const payload = {
        personalId: card.personal_id,
        authEmail: card.auth_email || null,
        status: card.status,
        nameEn: card.name_en || null,
        titleEn: card.title_en || null,
        companyEn: card.company_en || null,
        bioEn: card.bio_en || null,
        nameZhCht: card.name_zh_cht || null,
        titleZhCht: card.title_zh_cht || null,
        companyZhCht: card.company_zh_cht || null,
        bioZhCht: card.bio_zh_cht || null,
        nameZhChs: card.name_zh_chs || null,
        titleZhChs: card.title_zh_chs || null,
        companyZhChs: card.company_zh_chs || null,
        bioZhChs: card.bio_zh_chs || null,
        phone: card.phone || null,
        email: card.email || null,
        website: card.website || null,
        address: card.address || null,
        profileImage: card.profile_image || null,
        coverImage: card.cover_image || null,
        theme: card.theme || "default",
        customCss: card.custom_css || null,
      };

      if (isNew) {
        const result = await api.post<{ uid: number }>("/api/v1/cards", payload);
        // Save links if any
        if (links.length > 0) {
          await api.post(`/api/v1/cards/${result.uid}/links`, { links });
        }
        toast.success("Card created!");
        setDirty(false);
        navigate(`${basePath}/cards/${result.uid}`);
      } else {
        await api.put(`/api/v1/cards/${id}`, payload);
        // Save links
        await api.post(`/api/v1/cards/${id}/links`, { links });
        toast.success("Card saved!");
        setDirty(false);
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateBio = async (lang: string) => {
    setGenerating(true);
    try {
      const nameField = lang === "en" ? "name_en" : lang === "zh-cht" ? "name_zh_cht" : "name_zh_chs";
      const titleField = lang === "en" ? "title_en" : lang === "zh-cht" ? "title_zh_cht" : "title_zh_chs";
      const companyField = lang === "en" ? "company_en" : lang === "zh-cht" ? "company_zh_cht" : "company_zh_chs";
      const bioField = lang === "en" ? "bio_en" : lang === "zh-cht" ? "bio_zh_cht" : "bio_zh_chs";
      const { bio } = await api.post("/api/v1/ai-bio/generate", {
        name: card[nameField], title: card[titleField], company: card[companyField], language: lang,
      });
      setCard((prev: any) => ({ ...prev, [bioField]: bio }));
      setDirty(true);
      toast.success("Bio generated!");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setGenerating(false);
    }
  };

  // Link management
  const addLink = () => {
    setLinks(prev => [...prev, { type: "website", label: "", url: "" }]);
    setDirty(true);
  };

  const updateLink = (idx: number, field: keyof CardLink, value: string) => {
    setLinks(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
    setDirty(true);
  };

  const removeLink = (idx: number) => {
    setLinks(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
    </div>
  );

  const renderLangTab = (prefix: string, lang: string) => (
    <div className="grid gap-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1.5">Full Name</label>
          <input
            value={card[`name_${prefix}`] || ""}
            onChange={(e) => updateField(`name_${prefix}`, e.target.value)}
            placeholder="Jane Doe"
            className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1.5">Job Title</label>
          <input
            value={card[`title_${prefix}`] || ""}
            onChange={(e) => updateField(`title_${prefix}`, e.target.value)}
            placeholder="Product Manager"
            className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
          />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1.5">Company</label>
          <input
            value={card[`company_${prefix}`] || ""}
            onChange={(e) => updateField(`company_${prefix}`, e.target.value)}
            placeholder="Acme Corp"
            className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1.5">Department</label>
          <input
            value={card[`department_${prefix}`] || ""}
            onChange={(e) => updateField(`department_${prefix}`, e.target.value)}
            placeholder="Engineering"
            className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
          />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-sm font-medium text-muted-foreground">Bio</label>
          <button
            onClick={() => handleGenerateBio(lang)}
            disabled={generating}
            className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium transition-colors disabled:opacity-50"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {generating ? "Generating..." : "AI Generate"}
          </button>
        </div>
        <textarea
          value={card[`bio_${prefix}`] || ""}
          onChange={(e) => updateField(`bio_${prefix}`, e.target.value)}
          rows={4}
          placeholder="A short professional bio..."
          className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-muted-foreground mb-1.5">Address</label>
        <input
          value={card[`address_${prefix}`] || card.address || ""}
          onChange={(e) => updateField(prefix === "en" ? "address" : `address_${prefix}`, e.target.value)}
          placeholder="123 Main St, City"
          className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
        />
      </div>
    </div>
  );

  const displayName = card.name_en || card.name_zh_cht || card.name_zh_chs || "New Card";
  const isReadOnly = user?.role === "admin";

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => navigate(`${basePath}/cards`)}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-2 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Cards
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{isNew ? "New Card" : displayName}</h1>
            {!isNew && card.personal_id && (
              <a
                href={`/u/${card.personal_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <ExternalLink className="w-3 h-3" />
                Preview
              </a>
            )}
            {dirty && (
              <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full font-medium">
                Unsaved changes
              </span>
            )}
          </div>
        </div>
        {!isReadOnly && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2 shadow-sm transition-opacity"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : isNew ? "Create Card" : "Save Changes"}
          </button>
        )}
        {isReadOnly && (
          <span className="text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-lg">Read-only (Admin)</span>
        )}
      </div>

      {/* Identity Row */}
      <div className="bg-card rounded-xl border border-border p-5 mb-4">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Identity</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1.5">
              Personal ID <span className="text-destructive">*</span>
            </label>
            <input
              value={card.personal_id || ""}
              onChange={(e) => updateField("personal_id", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
              disabled={!isNew || isReadOnly}
              placeholder="jane-doe"
              className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow font-mono"
            />
            <p className="text-xs text-muted-foreground mt-1">Lowercase letters, numbers, hyphens only</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1.5">Auth Email</label>
            <input
              value={card.auth_email || ""}
              onChange={(e) => updateField("auth_email", e.target.value)}
              disabled={isReadOnly}
              placeholder="owner@example.com"
              className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1.5">Status</label>
            <select
              value={card.status || "active"}
              onChange={(e) => updateField("status", e.target.value)}
              disabled={isReadOnly}
              className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted rounded-xl p-1 mb-4 overflow-x-auto">
        {TABS.map((tab, i) => (
          <button
            key={tab}
            onClick={() => setActiveTab(i)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === i
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-card/50"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="bg-card rounded-xl border border-border p-5">
        {activeTab === 0 && renderLangTab("en", "en")}
        {activeTab === 1 && renderLangTab("zh_cht", "zh-cht")}
        {activeTab === 2 && renderLangTab("zh_chs", "zh-chs")}

        {activeTab === 3 && (
          <div className="grid gap-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Email</label>
                <input
                  value={card.email || ""}
                  onChange={(e) => updateField("email", e.target.value)}
                  disabled={isReadOnly}
                  type="email"
                  placeholder="jane@example.com"
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Phone</label>
                <input
                  value={card.phone || ""}
                  onChange={(e) => updateField("phone", e.target.value)}
                  disabled={isReadOnly}
                  type="tel"
                  placeholder="+1 234 567 8900"
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1.5">Website</label>
              <input
                value={card.website || ""}
                onChange={(e) => updateField("website", e.target.value)}
                disabled={isReadOnly}
                type="url"
                placeholder="https://example.com"
                className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1.5">Address</label>
              <input
                value={card.address || ""}
                onChange={(e) => updateField("address", e.target.value)}
                disabled={isReadOnly}
                placeholder="123 Main St, City, Country"
                className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
              />
            </div>
          </div>
        )}

        {activeTab === 4 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium">Social & Custom Links</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Add links that appear on the card</p>
              </div>
              {!isReadOnly && (
                <button
                  onClick={addLink}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-sm font-medium hover:bg-primary/20 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Link
                </button>
              )}
            </div>
            {links.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Link2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No links yet. Add social profiles or custom URLs.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {links.map((link, idx) => (
                  <div key={idx} className="flex gap-3 items-start bg-muted/40 rounded-lg p-3">
                    <select
                      value={link.type}
                      onChange={(e) => updateLink(idx, "type", e.target.value)}
                      disabled={isReadOnly}
                      className="px-2 py-2 rounded-lg border border-input bg-background text-sm w-36 shrink-0 focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      {LINK_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                    <input
                      value={link.label}
                      onChange={(e) => updateLink(idx, "label", e.target.value)}
                      disabled={isReadOnly}
                      placeholder="Label"
                      className="flex-1 px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
                    />
                    <input
                      value={link.url}
                      onChange={(e) => updateLink(idx, "url", e.target.value)}
                      disabled={isReadOnly}
                      placeholder="URL or value"
                      className="flex-1 px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
                    />
                    {!isReadOnly && (
                      <button
                        onClick={() => removeLink(idx)}
                        className="p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 5 && (
          <div className="grid gap-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Profile Photo URL</label>
                <input
                  value={card.profile_image || ""}
                  onChange={(e) => updateField("profile_image", e.target.value)}
                  disabled={isReadOnly}
                  placeholder="https://..."
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
                />
                {card.profile_image && (
                  <img src={card.profile_image} alt="Profile" className="mt-2 w-16 h-16 rounded-full object-cover border border-border" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">Cover Photo URL</label>
                <input
                  value={card.cover_image || ""}
                  onChange={(e) => updateField("cover_image", e.target.value)}
                  disabled={isReadOnly}
                  placeholder="https://..."
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
                />
                {card.cover_image && (
                  <img src={card.cover_image} alt="Cover" className="mt-2 w-full h-16 rounded-lg object-cover border border-border" />
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1.5">Card Theme</label>
              <select
                value={card.theme || "default"}
                onChange={(e) => updateField("theme", e.target.value)}
                disabled={isReadOnly}
                className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="default">Default</option>
                <option value="dark">Dark</option>
                <option value="minimal">Minimal</option>
                <option value="gradient">Gradient</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1.5">Custom CSS</label>
              <textarea
                value={card.custom_css || ""}
                onChange={(e) => updateField("custom_css", e.target.value)}
                disabled={isReadOnly}
                rows={5}
                placeholder="/* Custom styles for this card */"
                className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm font-mono resize-none disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
              />
            </div>
          </div>
        )}
      </div>

      {/* Sticky save bar on mobile */}
      {!isReadOnly && dirty && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 lg:hidden">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-semibold shadow-xl hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}
    </div>
  );
}
